<template>
  <b-table
    :data="revenueDataForTable"
    :striped="true"
    :loading="loading"
    :paginated="showPagination"
    pagination-position="both"
    :per-page="rowsPerPage"
    default-sort="shoppingCenterGroupName"
    default-sort-direction="asc"
  >
    <b-table-column
      v-slot="props"
      header-class="has-text-right"
      sortable
      field="shoppingCenterGroupName"
      :label="t('ShoppingCenterGroupName')"
      >{{
        props.row.shoppingCenterGroupName === props.row.shoppingCenterName
          ? t("DoesNotBelongToGroup")
          : props.row.shoppingCenterGroupName
      }}</b-table-column
    >
    <b-table-column
      v-slot="props"
      header-class="has-text-right"
      sortable
      field="shoppingCenterName"
      :label="t('ShoppingCenterName')"
      >{{ props.row.shoppingCenterName }}</b-table-column
    >
    <b-table-column
      v-slot="props"
      header-class="has-text-right"
      sortable
      field="shopName"
      :label="t('ShopName')"
      >{{ props.row.shopName }}</b-table-column
    >
    <b-table-column
      v-slot="props"
      header-class="has-text-right"
      sortable
      field="chainShopId"
      :label="t('ChainShopId')"
      >{{ props.row.chainShopId }}</b-table-column
    >
    <b-table-column
      v-slot="props"
      header-class="has-text-right"
      field="sections"
      :label="t('RevenueWithoutVat')"
    >
      <span
        v-for="(section) in props.row.sections"
        :key="`${props.row.shopId}-${section.sectionId}`"
        class="has-text-right"
      >
        <b-field
          :type="
            revenueExistsForShop(props.row.shopId, section.sectionId)
              ? 'is-info'
              : 'is-red'
          "
          :label="props.row.sections.length > 1 ? section.sectionName : ''"
        >
          <CurrencyRevenueInput
            :data-test-id="
              'revenueInput_'
                .concat(props.row.shopId.toString())
                .concat('_')
                .concat(section.sectionId.toString())
            "
            :shop-id="props.row.shopId"
            :section-id="section.sectionId"
            :is-disabled="props.row.isDisabled"
            :revenue-exists="revenueExistsForShop(props.row.shopId, section.sectionId)"
            :locale="locale"
            v-model="section.revenueValue"
            :tabindex="props.row.isDisabled ? -1 : 0"
            :ref="el => setInputRef(el, props.row.shopId, section.sectionId)"
          />
        </b-field>
      </span>
    </b-table-column>
    <template #empty>
      <section class="section" v-if="loading">
        <div class="content has-text-grey has-text-centered">
          <p>{{ t("Loading") }}</p>
        </div>
      </section>
      <section class="section" v-else-if="modelValue && modelValue.length === 0">
        <div class="content has-text-grey has-text-centered">
          <p>
            <b-icon pack="fas" icon="smile" size="is-large" />
          </p>
          <p>{{ t("AllShopsReported") }}</p>
        </div>
      </section>
      <section class="section" v-else>
        <div class="content has-text-grey has-text-centered">
          <p>
            <b-icon pack="fas" icon="frown-open" size="is-large" />
          </p>
          <p>{{ t("NoShops") }}</p>
        </div>
      </section>
    </template>
    <template #top-left>
      <b-field :label="t('ShopsPerPage')" type="is-blue">
        <b-select v-model="rowsPerPage" expanded @input="setPageSize">
          <option value="10">10</option>
          <option value="25">25</option>
          <option value="50">50</option>
          <option value="75">75</option>
          <option value="100">100</option>
        </b-select>
      </b-field>
    </template>
  </b-table>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import localStorageAccess from '@/resourceAccess/localStorageAccess'
import { useAppStore, type Shop } from '@/stores/app'
import CurrencyRevenueInput from '@/components/CurrencyRevenueInput.vue'

/**
 * Props interface for RevenueInputDetails component
 */
interface Props {
  /** Loading state indicator */
  loading?: boolean
  /** Search filter phrase */
  filterPhrase?: string
  /** Whether to show disabled shops */
  showDisabledShops?: boolean
  /** Whether to show shops that already have data */
  showShopsWithData?: boolean
  /** Array of shops for revenue input */
  modelValue?: Shop[] | null
  /** Selected date for revenue period */
  utcSelectedDate?: Date | null
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  filterPhrase: '',
  showDisabledShops: false,
  showShopsWithData: false,
  modelValue: null,
  utcSelectedDate: null,
})

/**
 * Component emits with proper typing
 */
const emit = defineEmits<{
  /** Emitted when modelValue changes (v-model) */
  'update:modelValue': [value: Shop[] | null]
  /** Emitted when filtered shop list changes */
  'shop-list-filtered': [filteredList: Shop[] | null]
}>()

// Composables
const { t, locale } = useI18n()
const appStore = useAppStore()

// Reactive data
const rowsPerPage = ref<number>(10)
const inputRefs: Ref<Map<string, any>> = ref(new Map())

/**
 * Validation result interface
 */
interface ValidationResult {
  isValid: boolean
  shopName?: string
  validationMessage?: string
}

interface PreparedShopFilterEntry {
  shop: Shop
  creationTime: number
  deactivationTime: number | null
  hasRevenueForAllSections: boolean
  searchText: string
}

const buildRevenueLookupKey = (shopId: number, sectionId: number): string =>
  `${shopId}:${sectionId}`

const revenueIndicationLookup = computed<Set<string>>(() => {
  const lookup = new Set<string>()
  const indications = appStore.getRevenueIndication?.data ?? []

  indications.forEach((shop) => {
    shop.sectionIds.forEach((sectionId) => {
      lookup.add(buildRevenueLookupKey(Number(shop.shopId), Number(sectionId)))
    })
  })

  return lookup
})

const normalizedSearchTerms = computed<string[]>(() => {
  return props.filterPhrase
    .toUpperCase()
    .split(/\s+/)
    .filter((term) => term.length > 0)
})

const preparedShopFilterEntries = computed<PreparedShopFilterEntry[] | null>(() => {
  if (!props.modelValue?.length) {
    return null
  }

  const indicationLookup = revenueIndicationLookup.value

  return props.modelValue.map((shop) => ({
    shop,
    creationTime: new Date(shop.creationDate).getTime(),
    deactivationTime: shop.deactivationDate ? new Date(shop.deactivationDate).getTime() : null,
    hasRevenueForAllSections:
      shop.sections?.every((section) =>
        indicationLookup.has(buildRevenueLookupKey(shop.shopId, section.sectionId))
      ) ?? false,
    searchText: [
      shop.shoppingCenterGroupName ?? '',
      shop.shoppingCenterName ?? '',
      shop.shopName,
      shop.shoppingCenterShopId ?? '',
    ]
      .join(' ')
      .toUpperCase(),
  }))
})

/**
 * Determines if pagination should be shown based on data length
 */
const showPagination = computed<boolean>(() => {
  return (props.modelValue?.length ?? 0) > 10
})

/**
 * Filters shop list based on date range, disabled status, revenue existence, and search terms
 */
const filteredShopList = computed<Shop[] | null>(() => {
  if (!preparedShopFilterEntries.value?.length || !props.utcSelectedDate) {
    return null
  }

  const startOfNextMonth = new Date(
    props.utcSelectedDate.getFullYear(),
    props.utcSelectedDate.getMonth() + 1,
    1
  )
  const startOfThisMonth = new Date(
    props.utcSelectedDate.getFullYear(),
    props.utcSelectedDate.getMonth(),
    1
  )
  const startOfNextMonthTime = startOfNextMonth.getTime()
  const startOfThisMonthTime = startOfThisMonth.getTime()
  const searchTerms = normalizedSearchTerms.value

  return preparedShopFilterEntries.value.reduce<Shop[]>((filteredShops, entry) => {
    const { shop, creationTime, deactivationTime, hasRevenueForAllSections, searchText } = entry

    if (
      !Number.isFinite(creationTime) ||
      (deactivationTime !== null && !Number.isFinite(deactivationTime)) ||
      (deactivationTime !== null && deactivationTime < startOfThisMonthTime) ||
      creationTime >= startOfNextMonthTime
    ) {
      return filteredShops
    }

    if (shop.isDisabled !== false && !props.showDisabledShops) {
      return filteredShops
    }

    if (hasRevenueForAllSections && !props.showShopsWithData) {
      return filteredShops
    }

    if (searchTerms.length > 0 && !searchTerms.every((term) => searchText.includes(term))) {
      return filteredShops
    }

    filteredShops.push(shop)
    return filteredShops
  }, [])
})

/**
 * Data formatted for table display
 */
const revenueDataForTable = computed<Shop[]>(() => {
  return filteredShopList.value ?? []
})

// Methods
/**
 * Saves the current page size selection to localStorage
 */
const setPageSize = (): void => {
  try {
    localStorageAccess.saveRowsPerPage('revenueInput', rowsPerPage.value)
  } catch (error) {
    console.warn('Failed to save page size preference:', error)
  }
}

/**
 * Manages input element references for validation
 * @param el - Vue component instance or null
 * @param shopId - Shop identifier
 * @param sectionId - Section identifier
 */
const setInputRef = (el: any, shopId: number, sectionId: number): void => {
  const key = `revenueInput_${shopId}_${sectionId}`
  if (el) {
    inputRefs.value.set(key, el)
  } else {
    inputRefs.value.delete(key)
  }
}

/**
 * Validates all revenue input fields
 * @returns Validation result with status and error details
 */
const validateRevenueInput = (): ValidationResult => {
  if (!props.modelValue) {
    return {
      isValid: false,
      shopName: t('Unknown'),
      validationMessage: t('NoShops'),
    }
  }

  for (const shop of props.modelValue) {
    if (!shop.sections) continue
    
    for (const section of shop.sections) {
      const inputKey = `revenueInput_${shop.shopId}_${section.sectionId}`
      const inputComponent = inputRefs.value.get(inputKey) as any
      
      if (inputComponent?.isValid === false) {
        return {
          isValid: false,
          shopName: shop.shopName,
          validationMessage: inputComponent.validationMessage || t('ValidationError'),
        }
      }
    }
  }

  return { isValid: true }
}

/**
 * Checks if revenue data already exists for a specific shop section
 * @param shopId - Shop identifier
 * @param sectionId - Section identifier
 * @returns True if revenue exists
 */
const revenueExistsForShop = (shopId: number, sectionId: number): boolean => {
  return revenueIndicationLookup.value.has(buildRevenueLookupKey(shopId, sectionId))
}

// Watchers

/**
 * Watch for modelValue changes and emit to parent
 */
watch(
  () => props.modelValue,
  (newValue) => {
    emit('update:modelValue', newValue)
  },
  { deep: true }
)

/**
 * Watch for filtered list changes and emit to parent
 */
watch(
  filteredShopList,
  (newValue) => {
    emit('shop-list-filtered', newValue)
  }
)

// Lifecycle hooks

/**
 * Load saved pagination preferences on component mount
 */
onMounted(() => {
  try {
    const savedRowsPerPage = localStorageAccess.getRowsPerPage('revenueInput')
    if (savedRowsPerPage) {
      const parsed = parseInt(savedRowsPerPage, 10)
      if (!isNaN(parsed) && parsed > 0) {
        rowsPerPage.value = parsed
      }
    }
  } catch (error) {
    console.warn('Failed to load page size preference:', error)
  }
})

// Expose public methods for parent components
defineExpose({
  /** Validates all revenue input fields */
  validateRevenueInput,
})
</script>
